import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GetRewardsQuery, GetRewardsQueryVariables } from '../../client/generated/graphql';
import { getRewardsQuery } from '../../client/queries/rewards';
import { ListReward } from '../../client/types';

export const useCachedReward = (rewardId?: string | null): ListReward | null => {
    const rewards = useCachedRewards();

    if (!rewardId) return null;
    return rewards.find(r => r.id === rewardId) ?? null;
};

export const useCachedCollectedCurrency = (): number | null => {
    const { data } = useQuery<GetRewardsQuery, GetRewardsQueryVariables>(getRewardsQuery, { fetchPolicy: 'cache-only' });
    return data?.user?.collectedCurrency ?? null;
};

export const useCachedRewards = (): ListReward[] => {
    const client = useApolloClient();

    return useMemo(() => {
        const cacheData = client.cache.extract();
        const allRewards = Object.entries(cacheData)
            .filter(([key, value]: [string, any]) => key.startsWith('Reward:') || value?.__typename === 'Reward')
            .map(([_key, value]) => value as ListReward);

        return allRewards;
    }, [client.cache]);
};

export const useCachedRewardsWithFallback = (): {
    rewards: ListReward[];
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const cachedRewards = useCachedRewards();

    // If nothing in cache, fetch all rewards
    const { data, loading, error } = useQuery<GetRewardsQuery>(getRewardsQuery, {
        skip: cachedRewards.length > 0,
        fetchPolicy: 'cache-first',
    });

    const rewards = cachedRewards.length > 0 ? cachedRewards : data?.rewards ?? [];

    return {
        rewards,
        loading: loading && cachedRewards.length === 0,
        error,
    };
};
